import { useMutation } from '@tanstack/react-query';
import { useWizardStore, type SplitMode, type MemberSlot } from '../wizardStore';
import { subscriptionApi } from '@/entities/subscription/api';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { WizardFooter } from '@/shared/ui/WizardFooter';
import { useT } from '@/shared/lib/i18n';
import { cn } from '@/shared/lib/cn';

function MemberRow({
  slot,
  onChange,
  onRemove,
  namePlaceholder,
  removeLabel,
}: {
  slot: MemberSlot;
  onChange: (patch: Partial<MemberSlot>) => void;
  onRemove: () => void;
  namePlaceholder: string;
  removeLabel: string;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-[13px] px-3 py-[11px] bg-white/50 border border-white/55">
      <span className="w-[30px] h-[30px] rounded-full bg-brand-gradient flex-shrink-0" />
      <input
        className="flex-1 bg-transparent text-[15px] font-medium outline-none placeholder:text-text-muted min-w-0"
        placeholder={namePlaceholder}
        value={slot.name}
        onChange={(e) => onChange({ name: e.target.value })}
      />
      <input
        type="text"
        inputMode="decimal"
        className="w-[80px] rounded-[9px] px-3 py-1.5 text-[16px] font-semibold bg-white/70 border border-white/70 outline-none text-right"
        placeholder="0"
        value={slot.amount}
        onChange={(e) => onChange({ amount: e.target.value.replace(/[^0-9.]/g, '') })}
      />
      <button
        onClick={onRemove}
        className="text-text-muted ml-1 p-1 flex-shrink-0 hover:text-status-debt transition-colors"
        aria-label={removeLabel}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export function SplitStep() {
  const { splitMode, memberSlots, totalPrice, subscriptionId, update, next } = useWizardStore();
  const t = useT();

  const total = parseFloat(totalPrice) || 0;
  const slotSum = memberSlots.reduce((s, m) => s + (parseFloat(m.amount) || 0), 0);
  const isValid = splitMode !== 'fixed' || memberSlots.length === 0 || Math.abs(slotSum - total) < 0.01;

  const patchMutation = useMutation({
    mutationFn: () => {
      if (!subscriptionId) { next(); return Promise.resolve(null); }
      return subscriptionApi.patch(subscriptionId, {
        split_mode: splitMode,
        member_slots: memberSlots.map((s) => ({ name: s.name, amount: parseFloat(s.amount) || 0 })),
        draft_step: 'preview',
      });
    },
    onSuccess: (result) => { if (result !== null) next(); },
  });

  const MODES: { value: SplitMode; label: string }[] = [
    { value: 'equal', label: t('wizard.split.equal') },
    { value: 'proportional', label: t('wizard.split.proportional') },
    { value: 'fixed', label: t('wizard.split.fixed') },
  ];

  const addSlot = () => {
    update({ memberSlots: [...memberSlots, { id: String(Date.now()), name: '', amount: '' }] });
  };

  const updateSlot = (id: string, patch: Partial<MemberSlot>) => {
    update({ memberSlots: memberSlots.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  };

  const removeSlot = (id: string) => {
    update({ memberSlots: memberSlots.filter((s) => s.id !== id) });
  };

  return (
    <div className="flex flex-col gap-4 flex-1">
      <h1 className="text-[24px] font-bold tracking-tight">{t('wizard.split.title')}</h1>

      <div className="flex gap-2">
        {MODES.map((m) => (
          <button
            key={m.value}
            className={cn(
              'flex-1 rounded-[11px] py-[9px] text-[13px] font-medium transition-all',
              splitMode === m.value
                ? 'text-white bg-brand-gradient shadow-[0_6px_14px_rgba(18,120,90,0.32)]'
                : 'bg-white/50 border border-white/60',
            )}
            onClick={() => update({ splitMode: m.value })}
          >
            {m.label}
          </button>
        ))}
      </div>

      {splitMode !== 'fixed' && (
        <div className="rounded-[13px] p-4 bg-white/40 border border-white/55 text-[14px] text-text-secondary">
          {splitMode === 'equal' ? t('wizard.split.equalInfo') : t('wizard.split.proportionalInfo')}
        </div>
      )}

      {splitMode === 'fixed' && (
        <div className="flex flex-col gap-2">
          {memberSlots.map((slot) => (
            <MemberRow
              key={slot.id}
              slot={slot}
              namePlaceholder={t('wizard.split.namePlaceholder')}
              removeLabel={t('common.delete')}
              onChange={(patch) => updateSlot(slot.id, patch)}
              onRemove={() => removeSlot(slot.id)}
            />
          ))}
          <button
            onClick={addSlot}
            className="flex items-center gap-2 rounded-[13px] px-3 py-[11px] border-2 border-dashed border-brand-green/40 text-[14px] text-text-secondary hover:bg-white/30 transition-colors"
          >
            <span className="w-[30px] h-[30px] rounded-full border-2 border-dashed border-brand-green/40 flex items-center justify-center text-brand-green text-[20px] leading-none">+</span>
            {t('wizard.split.addMember')}
          </button>

          {memberSlots.length > 0 && (
            <div
              className={cn(
                'flex items-center justify-between rounded-[13px] px-4 py-3',
                isValid
                  ? 'bg-status-paid/10 border border-status-paid/30'
                  : 'bg-status-debt/10 border border-status-debt/30',
              )}
            >
              <span className={cn('text-[14px]', isValid ? 'text-status-paid' : 'text-status-debt')}>
                {isValid
                  ? t('wizard.split.sumEqual', { sum: slotSum.toLocaleString('ru') })
                  : t('wizard.split.sumNotEqual', { sum: slotSum.toLocaleString('ru'), total: total.toLocaleString('ru') })}
              </span>
              {isValid && (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="text-status-paid">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex-1" />

      <WizardFooter>
        {patchMutation.isError && (
          <p className="text-[13px] text-status-debt text-center">{t('wizard.preview.error')}</p>
        )}
        <PrimaryButton
          onClick={() => patchMutation.mutate()}
          disabled={!isValid && memberSlots.length > 0}
          loading={patchMutation.isPending}
        >
          {t('wizard.continue')}
        </PrimaryButton>
      </WizardFooter>
    </div>
  );
}
